import React, { useEffect } from "react";
import { connect, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { ThunkDispatch } from "redux-thunk";

import { RealEstateType } from "../../constants";
import { formatDate, pad5, isExpiredAccount } from "../../libs";
import { accountsListActions } from "../../reducers/accountsList";
import { deleteAccountActions } from "../../reducers/deleteAccount";
import { filterAccountActions } from "../../reducers/filterAccount";
import { SortAscIcon, SortDescIcon, NoSortIcon } from "../SVGIcons";

import AccountListActionBar from "./AccountListActionBar";

interface AccountsListState {
  items: unknown[];
  page: number;
  pageCount: number;
  contractStartDateSort: "none" | "asc" | "desc";
  contractEndDateSort: "none" | "asc" | "desc";
}

function mapStateToProps(state: { accountsList: AccountsListState }) {
  return { ...state.accountsList };
}

interface AccountListProps {
  items: unknown[];
  page: number;
  pageCount: number;
  contractStartDateSort: "none" | "asc" | "desc";
  contractEndDateSort: "none" | "asc" | "desc";
}

const AccountList: React.FC<AccountListProps> = ({
  items,
  page,
  pageCount,
  contractStartDateSort,
  contractEndDateSort,
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch<ThunkDispatch<unknown, unknown, unknown>>();
  useEffect(() => {
    dispatch(accountsListActions.fetchAccounts());
    dispatch(filterAccountActions.getFilters());
  }, [dispatch]);

  const showPages = [page];
  if (page > 1) showPages.push(page - 1);
  if (page < pageCount) showPages.push(page + 1);
  showPages.sort();

  const setFirstPage = () => {
    if (page === 1) return;
    dispatch(accountsListActions.setPageAndFetch(1));
  };
  const setPreviousPage = () => {
    if (page === 1) return;
    dispatch(accountsListActions.setPageAndFetch(page - 1));
  };
  const setPage = (toPage: number) => {
    if (page === toPage) return;
    dispatch(accountsListActions.setPageAndFetch(toPage));
  };
  const setNextPage = () => {
    if (page === pageCount) return;
    dispatch(accountsListActions.setPageAndFetch(page + 1));
  };
  const setLastPage = () => {
    if (page === pageCount) return;
    dispatch(accountsListActions.setPageAndFetch(pageCount));
  };

  const switchSortContractStartDate = () => {
    dispatch(accountsListActions.switchSortContractStartDate());
  };
  const switchSortContractEndDate = () => {
    dispatch(accountsListActions.switchSortContractEndDate());
  };

  const goToViewAccount = (id: number) => {
    navigate(`/account-view/${id}`);
  };
  const goToEditAccount = (id: number) => {
    navigate(`/account-edit/${id}`);
  };
  const goToGeneralUserManage = (accountId: number) => {
    navigate(`/general-user-manage?account_id=${accountId}`);
  };
  const showConfirmDeleteAccount = (account: {
    id: number;
    corporate_name: string;
    is_active: boolean;
  }) => {
    const { id, corporate_name, is_active } = account;
    if (!is_active) return;

    dispatch(deleteAccountActions.setIsPopupShow(true));
    dispatch(deleteAccountActions.setId(id));
    dispatch(deleteAccountActions.setCorporateName(corporate_name));
  };

  const RealEstateTypeFlip = Object.fromEntries(
    Object.entries(RealEstateType).map(([key, value]) => [value, key]),
  );
  return (
    <>
      <AccountListActionBar />

      <div className="max-w-8xl w-full overflow-x-auto mx-auto text-[#2d2d2d] accountlist-wrapper">
        <table className="min-w-full border-collapse border border-gray-500 py-3 px-4">
          <colgroup>
            <col className="w-16" />
            <col className="w-36" />
            <col className="w-36" />
            <col className="w-36" />
            <col className="w-60" />
            <col />
            <col />
            <col />
            <col />
            <col />
            <col className="w-60" />
            <col />
            <col className="w-40" />
          </colgroup>
          <thead className="bg-[#5571ad] sticky top-0 z-10">
            <tr>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                ID
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap w-36">
                法人名
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                店舗名
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                代表者ユーザーID
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                代表者メールアドレス
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                不動産種別
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                利用プラン
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                発行回数/上限
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                <div className="flex gap-1 items-center w-full">
                  <span className="flex-1">契約開始日</span>
                  {contractStartDateSort === "none" && (
                    <NoSortIcon onClick={switchSortContractStartDate} />
                  )}
                  {contractStartDateSort === "asc" && (
                    <SortAscIcon onClick={switchSortContractStartDate} />
                  )}
                  {contractStartDateSort === "desc" && (
                    <SortDescIcon onClick={switchSortContractStartDate} />
                  )}
                </div>
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                <div className="flex gap-1 items-center w-full">
                  <span className="flex-1">契約終了日</span>
                  {contractEndDateSort === "none" && (
                    <NoSortIcon onClick={switchSortContractEndDate} />
                  )}
                  {contractEndDateSort === "asc" && (
                    <SortAscIcon onClick={switchSortContractEndDate} />
                  )}
                  {contractEndDateSort === "desc" && (
                    <SortDescIcon onClick={switchSortContractEndDate} />
                  )}
                </div>
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                備考
              </th>
              <th className="px-3 py-2 text-center text-white border border-[#889bc5] whitespace-nowrap">
                ステータス
              </th>
              <th className="sticky top-0 right-0 px-3 py-2 text-center text-white bg-[#5571ad] border border-[#889bc5] whitespace-nowrap">
                管理
              </th>
            </tr>
          </thead>
          <tbody className="bg-white text-sm">
            {items.map((item) => (
              <tr key={item.id} className="hover:bg-sky-50">
                <td className="p-2 border border-gray-300 text-center">
                  {pad5(item.id)}
                </td>
                <td className="p-2 border border-gray-300 whitespace-nowrap">
                  {item.corporate_name || ""}
                </td>
                <td className="p-2 border border-gray-300">
                  {item.store?.name || ""}
                </td>
                <td className="p-2 border border-gray-300">
                  {item.representative_user_name || ""}
                </td>
                <td className="p-2 border border-gray-300">
                  {item.representative_email || ""}
                </td>
                <td className="p-2 border border-gray-300 text-center">
                  {item.real_estate_type
                    ? RealEstateTypeFlip[item.real_estate_type]
                    : ""}
                </td>
                <td className="p-2 border border-gray-300 text-center">
                  {item.plan_type?.name || ""}
                </td>
                <td className="p-2 border border-gray-300 text-center">
                  {item.credit_consumed || 0}/{item.credit_limit || 0}
                </td>
                <td className="p-2 border border-gray-300 text-center">
                  {formatDate(item.contract_start_date)}
                </td>
                <td className="p-2 border border-gray-300 text-center">
                  {formatDate(item.contract_end_date)}
                </td>
                <td className="p-2 border border-gray-300">
                  {item.remarks || ""}
                </td>
                <td className="p-2 border border-gray-300 text-center">
                  {isExpiredAccount(item)}
                </td>
                <td className="sticky right-0 bg-white p-2 border border-gray-300">
                  <div className="flex items-center justify-center space-x-2">
                    <button
                      type="button"
                      onClick={() => goToGeneralUserManage(item.id)}
                      className="px-2 py-1 text-xs font-medium rounded text-white bg-orange-500 hover:bg-orange-400 whitespace-nowrap"
                    >
                      管理
                    </button>
                    <button
                      type="button"
                      onClick={() => goToViewAccount(item.id)}
                      className="px-2 py-1 text-xs font-medium rounded border border-blue-700 text-white bg-blue-700 hover:bg-blue-600 whitespace-nowrap"
                    >
                      詳細
                    </button>
                    <button
                      type="button"
                      onClick={() => goToEditAccount(item.id)}
                      className="px-2 py-1 text-xs font-medium rounded border border-blue-700 text-blue-700 bg-white hover:text-white hover:bg-blue-700 whitespace-nowrap"
                    >
                      編集
                    </button>
                    <button
                      type="button"
                      onClick={() => showConfirmDeleteAccount(item)}
                      className="px-2 py-1 text-xs font-medium rounded border border-red-700 text-red-600 bg-white hover:text-white hover:bg-red-700 whitespace-nowrap"
                    >
                      削除
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <nav className="flex justify-center py-2">
        <ol className="inline-flex -space-x-px text-base h-10">
          <li>
            <button
              type="button"
              onClick={setFirstPage}
              className="flex items-center justify-center px-4 h-10 ms-0 leading-tight text-gray-500 bg-white border border-e-0 border-gray-300 rounded-s-lg hover:bg-gray-100 hover:text-gray-700"
            >
              <span className="hidden md:inline-flex">最初のページ</span>
              <span className="md:hidden inline-flex">&laquo;</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={setPreviousPage}
              className="flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
            >
              <span className="hidden md:inline-flex">前へ</span>
              <span className="md:hidden inline-flex">&lt;</span>
            </button>
          </li>
          {showPages.map((showPage) => (
            <li key={showPage}>
              <button
                type="button"
                onClick={() => {
                  setPage(showPage);
                }}
                className={
                  page === showPage
                    ? "flex items-center justify-center px-4 h-10 text-blue-600 border border-gray-300 bg-blue-50 hover:bg-blue-100 hover:text-blue-700"
                    : "flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
                }
              >
                {showPage}
              </button>
            </li>
          ))}
          <li>
            <button
              type="button"
              onClick={setNextPage}
              className="flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-gray-300 hover:bg-gray-100 hover:text-gray-700"
            >
              <span className="hidden md:inline-flex">次へ</span>
              <span className="md:hidden inline-flex">&gt;</span>
            </button>
          </li>
          <li>
            <button
              type="button"
              onClick={setLastPage}
              className="flex items-center justify-center px-4 h-10 leading-tight text-gray-500 bg-white border border-gray-300 rounded-e-lg hover:bg-gray-100 hover:text-gray-700"
            >
              <span className="hidden md:inline-flex">最後のページ</span>
              <span className="md:hidden inline-flex">&raquo;</span>
            </button>
          </li>
        </ol>
      </nav>
    </>
  );
};

export default connect(mapStateToProps)(AccountList);
